import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from "react";

// Material-UI
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, Button, Grid, Theme, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { IconButton } from "src/components/IconButton/IconButton";
import { updateUserAppSettings } from "src/redux/actions/App.Actions";
import { setAircraftRowSettings } from "src/redux/actions/flights/FlightTimeline.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface ITimelineViewScale {
  setViewportScale: (scale: string) => void;
  viewScale: string;
  setTabValue?: React.Dispatch<React.SetStateAction<string | boolean>>;
  groupSave?: boolean;
}

type HeightOption = {
  value: number;
  label: string;
};

const TimelineViewScaleStyles = (theme: Theme): any => ({
  display: "flex",
  padding: "10px",
  flexWrap: "wrap",
  alignItems: "center",
  "& .scale_button": { margin: "0 5px" },
});

const rowHeightOptions = [
  { value: 1.2, label: "Very Thick (1.2)" },
  { value: 1.0, label: "Thick (1.0)" },
  { value: 0.8, label: "Standard (0.8)" },
  { value: 0.65, label: "Thin (0.65)" },
  { value: 0.5, label: "Very Thin (0.5)" },
];

/**
 * TIMELINE VIEW SCALE TOOL
 * Tool will display the nearest possible scale preset (when zooming with mouse and on mount)
 * Users can also click on button located below to jump to a selected timeline scale
 * For convenience, the center of the currently selected scale will be used for reference always.
 */
export const TimelineViewScale = forwardRef((props: ITimelineViewScale, ref: any): JSX.Element => {
  const { viewScale, setTabValue, groupSave, setViewportScale } = props;
  const theme = useTheme();
  const dispatch = useDispatch();

  const appSettings = useSelector((state: IRootState) => state.authentication.appSettings);
  const savedRowPercentage = appSettings?.flightTimelineRowHeightPercentage;
  const savedViewScale = appSettings?.flightTimelineViewScale;

  const rowPercentage = useSelector(
    (state: IRootState) => state.timelineFlights.aircraftRowSetting.rowHeightPercentage
  );
  const heightOption: HeightOption = useMemo(() => {
    return rowHeightOptions.find((each) => rowPercentage === each.value) ?? { value: 1.0, label: "Regular (1.0%)" };
  }, [rowPercentage]);

  useEffect(() => {
    if (groupSave) setViewportScale(savedViewScale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedViewScale, setViewportScale]);

  // Apply new row scale to the current app settings in redux
  const handleChangeRowScale = useCallback(
    (option: HeightOption) => dispatch(setAircraftRowSettings(option.value)),
    [dispatch]
  );

  const saveAsMyDefault = useCallback(async () => {
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Saving Row Height as Default...",
      successMessage: "Default Row Height Saved Successfully.",
      errorMessage: "Error: Unable to Save Row Height",
    };
    const response = await AmplifyAPIWrapper.Put<any>(
      "/me/settings",
      {
        flightTimelineRowHeightPercentage: rowPercentage,
        flightTimelineViewScale: viewScale,
      },
      !groupSave && notifications
    );
    dispatch(updateUserAppSettings(response, "flightTimelineRowHeightPercentage"));
    dispatch(updateUserAppSettings(response, "flightTimelineViewScale"));
  }, [dispatch, rowPercentage, viewScale, groupSave]);

  useImperativeHandle(ref, () => ({
    saveDefault() {
      return {
        flightTimelineRowHeightPercentage: rowPercentage,
        flightTimelineViewScale: viewScale,
      };
    },
  }));

  return (
    <Box sx={TimelineViewScaleStyles(theme)}>
      {!!setTabValue && (
        <IconButton style={{ paddingBottom: "4px" }} onClick={() => setTabValue(false)}>
          <VisibilityOffIcon fontSize="small" />
        </IconButton>
      )}
      <Typography variant="subtitle1" className="body_text">
        Timeline View Scale
      </Typography>
      <Button
        id={"set_viewscale_hour_btn"}
        className="scale_button"
        variant={viewScale === "hour" ? "contained" : "outlined"}
        onClick={() => props.setViewportScale("hour")}
      >
        Hour
      </Button>
      <Button
        id={"set_viewscale_day_btn"}
        className="scale_button"
        variant={viewScale === "day" ? "contained" : "outlined"}
        onClick={() => props.setViewportScale("day")}
      >
        Day
      </Button>
      <Button
        id={"set_viewscale_week_btn"}
        className="scale_button"
        variant={viewScale === "week" ? "contained" : "outlined"}
        onClick={() => props.setViewportScale("week")}
      >
        Week
      </Button>
      <Button
        id={"set_viewscale_month_btn"}
        className="scale_button"
        variant={viewScale === "month" ? "contained" : "outlined"}
        onClick={() => props.setViewportScale("month")}
      >
        Month
      </Button>
      <Button
        id={"set_viewscale_3month_btn"}
        className="scale_button"
        variant={viewScale === "3month" ? "contained" : "outlined"}
        onClick={() => props.setViewportScale("3month")}
      >
        3 Months
      </Button>

      <Grid item style={{ width: 263 }}>
        <SelectField
          name="rowheight_selector"
          className="rowheight_selector"
          label="Aircraft Row Height"
          options={rowHeightOptions}
          onChange={handleChangeRowScale}
          value={heightOption}
          menuPosition="fixed"
          isMulti={false}
          openMenuOnFocus={true}
          isClearable={false}
          // placeholder="rowheight..."
          maxMenuHeight={400}
        />
      </Grid>
      {!groupSave && (
        <Button
          id={"deselect_btn"}
          disabled={rowPercentage === savedRowPercentage && savedViewScale === viewScale}
          variant="outlined"
          onClick={saveAsMyDefault}
          className="save_as_default_filter"
        >
          {"Save as my default"}
        </Button>
      )}
    </Box>
  );
});
